import { LocationStrategy } from '@angular/common';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TargetType } from '@libs/shared/models';
import { ProductHrefSource } from '@libs/shop/models';
import { LinkRouter } from './link-router.model';

@Injectable({ providedIn: 'root' })
export class AppLinkRouterService implements LinkRouter {
  protected readonly locationStrategy: LocationStrategy = inject(LocationStrategy);
  protected readonly fragmentNavigationEnabled: boolean = true;

  private readonly router: Router = inject(Router);
  private readonly document: Document = inject(DOCUMENT);

  //eslint-disable-next-line @typescript-eslint/require-await
  public async formatHref(url: string): Promise<string> {
    // External and fragment links should not be modified
    if (this.isExternalUrl(url) || this.isFragmentNavigation(url)) {
      return url;
    }

    // Using location strategy makes it compatible with custom APP_BASE_HREF
    return this.locationStrategy.prepareExternalUrl(
      this.normalizeUrl(url),
    );
  }

  public getProductRawHref(product: ProductHrefSource): string {
    if (product.bundleType === 'open') {
      return `/configurable-bundles/${product.bundle?.templateId}`;
    } else {
      return `/product/${product.sku}`;
    }
  }

  public navigate(href: string, target?: TargetType): void {
    if (this.isFragmentNavigation(href, target)) {
      this.navigateFragment(href.slice(1));

      return;
    }

    const isInternal: boolean = href.startsWith(this.locationStrategy.getBaseHref());

    if (isInternal && target !== '_blank') {
      this.navigateInternal(href);
    } else {
      this.navigateExternal(href, target);
    }
  }

  private navigateInternal(href: string): void {
    const url: string = href.replace(this.locationStrategy.getBaseHref(), '');

    void this.router.navigateByUrl(url);
  }

  private navigateFragment(fragment: string): void {
    const currentUrl: string = this.router.url.split('#')[0] || '/';

    void this.router.navigateByUrl(`${currentUrl}#${fragment}`, {
      onSameUrlNavigation: 'reload',
      skipLocationChange: true,
    });
  }

  private navigateExternal(href: string, target?: TargetType): void {
    this.document.defaultView?.open(href, target);
  }

  private isExternalUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  private isFragmentNavigation(href: string, target?: TargetType): boolean {
    return this.fragmentNavigationEnabled
      && href.startsWith('#')
      && href.length > 1
      && target !== '_blank';
  }

  private normalizeUrl(url: string): string {
    // Some URL paths may contain parentheses (e.g. '/foo-(bar)-baz'),
    // which are reserved by Angular router for named outlets.
    // @see: https://github.com/angular/angular/issues/50787#issuecomment-1600589720
    return url.replace(/[()]/g, (char) => {
      return `%${char.charCodeAt(0).toString(16)}`;
    });
  }
}
